// Targets (Individual + Team) client — SUN-327 Lane W. Types mirror the server
// contract (backend leads/targets/target-card.ts, D6/D7/D8); do not rename fields.
import { api } from '@/utils/api';

export type TargetKind = 'manager' | 'coordinator' | 'rep';
export type TargetProductGroup = 'SS_TANKS' | 'DOMESTIC_HP' | 'COMMERCIAL_HP' | 'SS_PLUMBING';
export type TargetScope = 'individual' | 'team';

export interface TargetLine {
  targetLitres: number | null;
  achievedLitres: number | null;
  targetRupees: number | null;
  achievedRupees: number;
  /** 0-100, one decimal, on the product's own unit */
  pct: number;
}

export interface ProductTargetBlock {
  productGroup: TargetProductGroup;
  label: string;
  unit: 'L' | 'INR';
  team: TargetLine | null;
  individual: TargetLine | null;
}

export interface PickerPerson {
  id: string;
  name: string;
  kind: TargetKind;
  /** 0 = the manager */
  depth: number;
  isViewer: boolean;
}

export interface TargetMonth {
  key: string;
  label: string;
  longLabel: string;
  fiscalYear: string;
  periodIndex: number;
  quarterIndex: number;
  start: string;
  end: string;
  isCurrent: boolean;
  isClosed: boolean;
  isStarted: boolean;
}

export interface TargetCardDto {
  month: TargetMonth;
  viewer: { id: string; kind: TargetKind };
  person: { id: string; name: string; kind: TargetKind } | null;
  teamOwner: { id: string; name: string; firstName: string } | null;
  hidden: boolean;
  products: ProductTargetBlock[];
  totals: { individual: TargetLine | null; team: TargetLine | null };
  noOwnTarget: boolean;
  locked: boolean;
}

export interface TeamQuotaMember {
  id: string;
  name: string;
  kind: TargetKind;
  achievedRupees: number;
  ownTarget: TargetLine | null;
  onBehalfCount: number;
}
export interface TeamQuotaDto extends TargetCardDto {
  members: TeamQuotaMember[];
}

/** Same split the server uses (targetKindOf): role name → manager / coordinator / rep. */
const MANAGER_ROLES = ['sr_sales_manager', 'sales_manager', 'manager_sales', 'operations_manager'];
export function targetKindOfRole(roleName: string | null | undefined): TargetKind {
  // Accepts the role key (sr_sales_manager) or its label (Sr. Sales Manager).
  const key = (roleName ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (key === 'coordinator') return 'coordinator';
  return MANAGER_ROLES.includes(key) ? 'manager' : 'rep';
}

/** The server answers { success, data: { card | quota | people } }; api.get does not unwrap it, so read data.data.
 *  `personId` null/undefined → server default (viewer's own card); 'all' → Team (all). */
function params(month?: string | null, personId?: string | null) {
  const p: Record<string, string> = {};
  if (month) p.month = month;
  if (personId) p.personId = personId;
  return p;
}

export async function getMine(month?: string | null, personId?: string | null): Promise<{ card: TargetCardDto | null }> {
  const { data } = await api.get('/sales-targets/mine', { params: params(month, personId) });
  return { card: data?.data?.card ?? null };
}

export async function getTeam(month?: string | null, personId?: string | null): Promise<{ quota: TeamQuotaDto | null }> {
  const { data } = await api.get('/sales-targets/team', { params: params(month, personId) });
  return { quota: data?.data?.quota ?? null };
}

export async function getPeople(month?: string | null): Promise<PickerPerson[]> {
  const { data } = await api.get('/sales-targets/people', { params: params(month) });
  return data?.data?.people ?? [];
}

// ── Setting side (D7) ──
export interface SetProductValues { litres: number | null; rupees: number | null }
export interface SetPerson {
  id: string;
  name: string;
  designation: string;
  kind: TargetKind;
  reportsToName: string | null;
  individual: Record<TargetProductGroup, SetProductValues> | null;
  team: Record<TargetProductGroup, SetProductValues> | null;
}
export interface SetPeopleResponse {
  month: TargetMonth;
  groups: Array<{ head: SetPerson | null; members: SetPerson[] }>;
}

export async function getSetPeople(month: string): Promise<SetPeopleResponse> {
  const { data } = await api.get('/analytics/scorecard/targets/people', { params: { month } });
  return data?.data ?? { month: null, groups: [] };
}

export interface PutTargetInput {
  userId: string;
  fiscalYear: string;
  periodType: 'MONTHLY' | 'QUARTERLY';
  periodIndex: number;
  productGroup: TargetProductGroup;
  scope: TargetScope;
  targetLitres?: number | null;
  targetRupees?: number | null;
  override?: boolean;
}

export async function putTarget(input: PutTargetInput) {
  const { data } = await api.put('/analytics/scorecard/targets', input);
  return data;
}

// ── Team drawer (D8) ──
export interface UserTeamDto {
  reports: Array<{ id: string; name: string; kind: TargetKind; note: string | null }>;
  count: number;
  targets: { individualRupees: number | null; teamRupees: number | null };
}

export async function getUserTeam(userId: string, month?: string | null): Promise<UserTeamDto> {
  const { data } = await api.get(`/auth/users/${userId}/team`, { params: params(month) });
  return data?.data ?? data;
}

// ── Formatting shared by every target view ──
export const fmtLitres = (n: number | null | undefined): string =>
  `${Math.round(n ?? 0).toLocaleString('en-IN')} L`;

/** Indian lakhs: 550000 → ₹5.5 L; under a lakh → ₹12,000. */
export function fmtRupeesShort(n: number | null | undefined): string {
  const v = n ?? 0;
  if (Math.abs(v) >= 100000) return `₹${parseFloat((v / 100000).toFixed(2))} L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
}

/** Whole-number percent, half-up, from `pct`. */
export const fmtPct = (pct: number): string => `${Math.floor(pct + 0.5)}%`;
